import React, { useState, useEffect, useCallback, useContext, useMemo } from "react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as ChartTooltip,
  ResponsiveContainer, PieChart, Pie, Cell, Legend, BarChart, Bar
} from "recharts";
import {
  makeStyles, Paper, Typography, Grid, CircularProgress,
  TextField, Button, MenuItem, Select, FormControl, InputLabel,
  Divider, Table, TableHead, TableBody, TableRow, TableCell,
  Collapse
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import AssessmentIcon from "@material-ui/icons/Assessment";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import HourglassEmptyIcon from "@material-ui/icons/HourglassEmpty";
import TimerIcon from "@material-ui/icons/Timer";
import BlockIcon from "@material-ui/icons/Block";
import ChatIcon from "@material-ui/icons/Chat";
import GetAppIcon from "@material-ui/icons/GetApp";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { asArray } from "../../helpers/asArray";
import { fmtDate } from "../../translate/localeHelper";
import { AuthContext } from "../../context/Auth/AuthContext";

// feat_379: el Dashboard entra entero en la pantalla de una PC (≥ ~1280×720)
// sin scrollear: filtros arriba en una fila compacta, y las dos filas de
// paneles reparten el alto que queda (flex). Debajo de `md` (tablet/móvil)
// los paneles se apilan con alto fijo y `root` scrollea como antes.
// Si la ventana es muy baja, los `minHeight` de las filas ganan y vuelve el
// scroll en vez de aplastar los gráficos.
const useStyles = makeStyles(theme => ({
  // feat_378: MainContainer recorta con overflowY:hidden + height:100%, así que
  // el scroll vertical (cuando hace falta) tiene que vivir acá.
  root: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    ...theme.scrollbarStyles,
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
    padding: theme.spacing(0, 2, 2, 2),
    backgroundColor: theme.palette.background.default,
    [theme.breakpoints.down("sm")]: {
      "& > *": { flexShrink: 0 },
    },
  },
  topBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    flexShrink: 0,
  },
  banner: {
    flex: 1,
    minWidth: 0,
    padding: theme.spacing(0.75, 1.5),
    borderLeft: `4px solid ${theme.palette.primary.main}`,
    backgroundColor: theme.palette.type === "dark" ? "#1a2744" : "#e8f0fe",
  },
  toggle: { flexShrink: 0, whiteSpace: "nowrap" },
  filters: {
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    alignItems: "center",
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.background.paper,
    borderRadius: 8,
  },
  // El wrap se decide con el flex-basis (no con minWidth): basis = el mínimo
  // usable, y después crecen hasta llenar el renglón. Así a 1280 con sidebar
  // fijo los 7 controles entran en UN renglón.
  dateField: { flex: "1 1 138px", minWidth: 138, maxWidth: 190 },
  selectField: { flex: "1 1 115px", minWidth: 115, maxWidth: 240 },
  reasonField: { flex: "1 1 160px", minWidth: 160, maxWidth: 260 },
  // Label en un renglón con "…" si no entra (selectores por elemento: en el
  // build de prod las clases Mui* llevan sufijo y `.MuiInputLabel-root` no matchea).
  oneLineLabel: {
    "& label:not([data-shrink=\"true\"])": {
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      maxWidth: "calc(100% - 40px)",
    },
  },
  filterButton: { flex: "0 0 auto", whiteSpace: "nowrap" },
  cardsRow: { flexShrink: 0 },
  card: {
    padding: theme.spacing(1, 1.25),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.25),
    borderRadius: 10,
    height: "100%",
    boxSizing: "border-box",
  },
  cardIcon: {
    width: 32,
    height: 32,
    flexShrink: 0,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#fff",
  },
  // nowrap: un valor partido en dos renglones ("293 / min") estiraba toda la fila.
  cardText: { minWidth: 0 },
  cardValue: { fontSize: "1.35rem", fontWeight: 700, lineHeight: 1, whiteSpace: "nowrap" },
  cardLabel: { color: theme.palette.text.secondary, fontSize: "0.78rem", marginTop: 2, lineHeight: 1.2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  // Fila de paneles: reparte el alto sobrante. nowrap es lo que hace que el
  // alto lo mande la fila y no el contenido: con el wrap del Grid, el alto de
  // la línea lo fijaba la tabla más larga y la fila se pasaba de la pantalla.
  panelRow: {
    flex: "1 1 0",
    minHeight: 170,
    flexWrap: "nowrap",
    [theme.breakpoints.down("sm")]: { flex: "0 0 auto", minHeight: 0, flexWrap: "wrap" },
  },
  panelItem: {
    display: "flex",
    [theme.breakpoints.down("sm")]: { height: 300 },
  },
  chartPaper: {
    flex: 1,
    minWidth: 0,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    padding: theme.spacing(1.25, 1.5),
    borderRadius: 10,
  },
  chartTitle: { fontWeight: 600, marginBottom: theme.spacing(0.5), lineHeight: 1.3 },
  // ResponsiveContainer necesita un padre con alto definido: el absoluto toma
  // el alto que el flex le dio a `chartBox` sin realimentarlo.
  chartBox: { flex: 1, minHeight: 0, position: "relative" },
  chartFill: { position: "absolute", top: 0, right: 0, bottom: 0, left: 0 },
  tableBox: { flex: 1, minHeight: 0, overflowY: "auto", ...theme.scrollbarStyles, "& th": { whiteSpace: "nowrap" } },
  loader: { display: "flex", justifyContent: "center", padding: theme.spacing(6) },
  queueDot: { width: 10, height: 10, borderRadius: "50%", display: "inline-block", marginRight: 6 },
}));

const PIE_COLORS = ["#667eea", "#f59e0b", "#10b981", "#ef4444", "#8b5cf6", "#ec4899"];

const MetricCard = ({ icon, iconBg, value, label }) => {
  const classes = useStyles();
  return (
    <Paper className={classes.card} elevation={0}>
      <div className={classes.cardIcon} style={{ background: iconBg }}>{icon}</div>
      <div className={classes.cardText}>
        <div className={classes.cardValue}>{value ?? "—"}</div>
        <div className={classes.cardLabel}>{label}</div>
      </div>
    </Paper>
  );
};

const toDateStr = (date) => date.toISOString().split("T")[0];

const escapeCSV = (v) => {
  if (v === null || v === undefined) return "";
  const s = String(v).replace(/"/g, '""');
  return s.includes(",") || s.includes('"') || s.includes("\n") ? `"${s}"` : s;
};

const exportCSV = (data) => {
  const rows = [];

  rows.push(["=== RESUMEN ===", ""]);
  rows.push(["Total chats", data.total]);
  rows.push(["Cerrados", data.closed]);
  rows.push(["Abiertos", data.open]);
  rows.push(["Pendientes", data.pending]);
  rows.push(["Sin respuesta", data.noResponse ?? ""]);
  rows.push(["Resp. promedio (min)", data.avgResponseMinutes]);
  rows.push(["Mensajes IA", data.aiMessages]);
  rows.push(["Mensajes humano", data.humanMessages]);

  if (data.ticketsDetail?.length) {
    rows.push([]);
    rows.push(["=== DETALLE DE CHATS ===", "", "", "", "", "", "", "", "", ""]);
    rows.push([
      "ID", "Contacto", "Teléfono", "Cola", "Motivo de contacto",
      "Asesor", "Estado", "Fecha creación", "Fecha resolución",
      "Tiempo resolución (min)", "Atendido por IA", "Resumen IA"
    ]);
    data.ticketsDetail.forEach(t => {
      const created = t.createdAt ? new Date(t.createdAt) : null;
      const resolved = t.updatedAt && t.status === "closed" ? new Date(t.updatedAt) : null;
      const resolutionMin = (created && resolved)
        ? Math.round((resolved - created) / 60000)
        : "";
      rows.push([
        t.id,
        t.Contact?.name || t.contact?.name || "",
        t.Contact?.number || t.contact?.number || "",
        t.Queue?.name || t.queue?.name || "",
        t.ContactReason?.name || t.contactReason?.name || "",
        t.User?.name || t.user?.name || "",
        t.status || "",
        created ? created.toLocaleDateString("es-AR") + " " + created.toLocaleTimeString("es-AR") : "",
        resolved ? resolved.toLocaleDateString("es-AR") + " " + resolved.toLocaleTimeString("es-AR") : "",
        resolutionMin,
        (t.respondedByAi ? "Sí" : "No"),
        t.Contact?.aiSummary || t.contact?.aiSummary || ""
      ]);
    });
  }

  const csv = rows.map(r => r.map(escapeCSV).join(",")).join("\n");
  const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `dashboard_${new Date().toISOString().split("T")[0]}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

const Reports = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);

  const today = new Date();
  const thirtyAgo = new Date(); thirtyAgo.setDate(today.getDate() - 30);

  const [startDate, setStartDate] = useState(toDateStr(thirtyAgo));
  const [endDate, setEndDate] = useState(toDateStr(today));
  const [users, setUsers] = useState([]);
  const [queues, setQueues] = useState([]);
  const [reasons, setReasons] = useState([]);
  const [selectedUser, setSelectedUser] = useState(String(user?.id ?? ""));
  const [selectedQueue, setSelectedQueue] = useState("");
  const [selectedReason, setSelectedReason] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const isAdmin = user?.profile === "admin" || user?.profile === "superadmin";
  const [filtersOpen, setFiltersOpen] = useState(isAdmin);

  const selectedUserName = useMemo(() => {
    if (!selectedUser || selectedUser === String(user?.id)) return user?.name || "";
    const found = users.find(u => String(u.id) === String(selectedUser));
    return found ? found.name : user?.name || "";
  }, [selectedUser, users, user]);

  useEffect(() => {
    const fetchMeta = async () => {
      try {
        const [usersRes, queuesRes, reasonsRes] = await Promise.all([
          api.get("/users"),
          api.get("/queue"),
          api.get("/contact-reasons")
        ]);
        // ⛔ Acá vivía `usersRes.data?.users || usersRes.data || []`. El
        // segundo término guardaba el OBJETO ENTERO de la respuesta cuando
        // faltaba la clave `users`, y el `users.map()` de más abajo tiraba
        // `TypeError: c.map is not a function` — la pantalla de `/`, o sea
        // WAIA entera para el que recién entra. `GET /users` devuelve
        // `{users, count, hasMore}` (UserController.index) y nunca devolvió un
        // array pelado: ese fallback cubría una forma que el endpoint no tiene.
        setUsers(asArray(usersRes.data?.users, "GET /users → data.users"));
        setQueues(asArray(queuesRes.data, "GET /queue"));
        setReasons(asArray(reasonsRes.data, "GET /contact-reasons"));
      } catch (err) { /* ignore */ }
    };
    fetchMeta();
  }, []);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ startDate, endDate });
      if (selectedUser) params.set("userId", selectedUser);
      if (selectedQueue) params.set("queueId", selectedQueue);
      if (selectedReason) params.set("reasonId", selectedReason);
      const { data: res } = await api.get(`/reports/summary?${params}`);
      setData(res);
    } catch (err) {
      toastError(err);
    }
    setLoading(false);
  }, [startDate, endDate, selectedUser, selectedQueue, selectedReason]);

  useEffect(() => { fetchReport(); }, []);

  const pieData = data
    ? [
        { name: i18n.t("reports.ia"), value: data.aiMessages || 0 },
        { name: i18n.t("reports.humano"), value: data.humanMessages || 0 }
      ]
    : [];

  const statusPie = data
    ? [
        { name: i18n.t("reports.cerrados"), value: data.closed || 0 },
        { name: i18n.t("reports.abiertos"), value: data.open || 0 },
        { name: i18n.t("reports.pendientes"), value: data.pending || 0 },
      ]
    : [];

  // Los % van en la leyenda: las etiquetas sobre las porciones no entran
  // cuando el panel se achica para que todo quepa en pantalla.
  const pctLegend = slices => {
    const total = slices.reduce((s, x) => s + x.value, 0);
    return value => {
      const slice = slices.find(x => x.name === value);
      return total > 0 && slice ? `${value} ${Math.round(slice.value / total * 100)}%` : value;
    };
  };

  const byUser = data ? asArray(data.byUser, "informe → byUser") : [];
  const byQueue = data ? asArray(data.byQueue, "informe → byQueue") : [];
  const byReason = data ? asArray(data.byReason, "informe → byReason") : [];
  // Fila de abajo: los paneles que haya se reparten el ancho en partes iguales.
  const bottomPanels = data
    ? [
        "ia",
        byUser.length > 0 && "user",
        byQueue.length > 0 && "queue",
        byReason.length > 0 && "reason",
      ].filter(Boolean)
    : [];
  const bottomMd = 12 / bottomPanels.length;
  const bottomSm = bottomPanels.length > 1 ? 6 : 12;

  const renderBottomPanel = key => {
    if (key === "ia") return (
      <Paper className={classes.chartPaper} elevation={0}>
        <Typography variant="subtitle2" className={classes.chartTitle}>
          {i18n.t("reports.mensajesIAvsHumano")}
        </Typography>
        {pieData[0].value === 0 && pieData[1].value === 0 ? (
          <Typography variant="body2" color="textSecondary">{i18n.t("reports.sinDatos")}</Typography>
        ) : (
          <div className={classes.chartBox}>
            <div className={classes.chartFill}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="45%" innerRadius="45%" outerRadius="75%"
                    dataKey="value" paddingAngle={4}
                  >
                    {pieData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i]} />)}
                  </Pie>
                  <Legend formatter={pctLegend(pieData)} />
                  <ChartTooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </Paper>
    );
    if (key === "user") return (
      <Paper className={classes.chartPaper} elevation={0}>
        <Typography variant="subtitle2" className={classes.chartTitle}>
          {i18n.t("reports.chatsPorAgente")}
        </Typography>
        <div className={classes.chartBox}>
          <div className={classes.chartFill}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={byUser} layout="vertical" margin={{ top: 0, left: 0, right: 12, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={90} />
                <ChartTooltip formatter={v => [v, i18n.t("reports.chats")]} />
                <Bar dataKey="count" fill="#10b981" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </Paper>
    );
    const isQueue = key === "queue";
    const rows = isQueue ? byQueue : byReason;
    return (
      <Paper className={classes.chartPaper} elevation={0}>
        <Typography variant="subtitle2" className={classes.chartTitle}>
          {i18n.t(isQueue ? "reports.chatsPorCola" : "reports.chatsPorMotivo")}
        </Typography>
        <div className={classes.tableBox}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>{i18n.t(isQueue ? "reports.cola" : "reports.motivo")}</TableCell>
                <TableCell align="right">{i18n.t("reports.chats")}</TableCell>
                <TableCell align="right">{i18n.t("reports.pctTotal")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(r => (
                <TableRow key={isQueue ? r.queueId : r.reasonId}>
                  <TableCell>
                    {isQueue && <span className={classes.queueDot} style={{ backgroundColor: r.color }} />}
                    {r.name}
                  </TableCell>
                  <TableCell align="right">{r.count}</TableCell>
                  <TableCell align="right">
                    {data.total > 0 ? `${Math.round(r.count / data.total * 100)}%` : "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Paper>
    );
  };

  return (
    <MainContainer>
      <MainHeader>
        <Title>{i18n.t("dashboard.titulo")}</Title>
      </MainHeader>

      <div className={classes.root}>
        {/* Banner descriptivo + mostrar/ocultar filtros en el mismo renglón */}
        <div className={classes.topBar}>
          <Paper className={classes.banner} elevation={0}>
            <Typography variant="body2">
              {i18n.t("dashboard.summaryBanner", {
                from: fmtDate(startDate),
                to: fmtDate(endDate),
                userName: selectedUserName
              })}
            </Typography>
          </Paper>
          <Button
            size="small"
            className={classes.toggle}
            onClick={() => setFiltersOpen(o => !o)}
            startIcon={filtersOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          >
            {filtersOpen ? i18n.t("dashboard.filters.hide") : i18n.t("dashboard.filters.show")}
          </Button>
        </div>

        {/* Filtros — arriba, para que se vean sin scrollear */}
        <Collapse in={filtersOpen} style={{ flexShrink: 0 }}>
          <div className={classes.filters}>
            <TextField
              className={classes.dateField}
              label={i18n.t("reports.desde")}
              type="date"
              size="small"
              variant="outlined"
              value={startDate}
              onChange={e => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              className={classes.dateField}
              label={i18n.t("reports.hasta")}
              type="date"
              size="small"
              variant="outlined"
              value={endDate}
              onChange={e => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <FormControl variant="outlined" size="small" className={`${classes.selectField} ${classes.oneLineLabel}`}>
              <InputLabel shrink>{i18n.t("reports.agente")}</InputLabel>
              <Select
                value={selectedUser}
                onChange={e => setSelectedUser(e.target.value)}
                label={i18n.t("reports.agente")}
                displayEmpty
              >
                <MenuItem value=""><em>{i18n.t("reports.todos")}</em></MenuItem>
                {users.map(u => (
                  <MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl variant="outlined" size="small" className={`${classes.selectField} ${classes.oneLineLabel}`}>
              <InputLabel>{i18n.t("reports.cola")}</InputLabel>
              <Select
                value={selectedQueue}
                onChange={e => setSelectedQueue(e.target.value)}
                label={i18n.t("reports.cola")}
              >
                <MenuItem value=""><em>{i18n.t("reports.todas")}</em></MenuItem>
                {queues.map(q => (
                  <MenuItem key={q.id} value={q.id}>{q.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl variant="outlined" size="small" className={`${classes.reasonField} ${classes.oneLineLabel}`}>
              <InputLabel>{i18n.t("reports.motivoContacto")}</InputLabel>
              <Select
                value={selectedReason}
                onChange={e => setSelectedReason(e.target.value)}
                label={i18n.t("reports.motivoContacto")}
              >
                <MenuItem value=""><em>{i18n.t("reports.todos")}</em></MenuItem>
                {reasons.map(r => (
                  <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button className={classes.filterButton} variant="contained" color="primary" onClick={fetchReport} disabled={loading}>
              {loading ? <CircularProgress size={18} /> : i18n.t("reports.generar")}
            </Button>
            {data && (
              <Button
                className={classes.filterButton}
                variant="outlined"
                startIcon={<GetAppIcon />}
                onClick={() => exportCSV(data)}
              >
                {i18n.t("reports.exportarCSV")}
              </Button>
            )}
          </div>
        </Collapse>

        {loading && !data && (
          <div className={classes.loader}><CircularProgress /></div>
        )}

        {data && (
          <>
            {/* Metric Cards */}
            <Grid container spacing={2} className={classes.cardsRow}>
              <Grid item xs={6} sm={4} md={2}>
                <MetricCard icon={<ChatIcon />} iconBg="#667eea" value={data.total} label={i18n.t("reports.totalChats")} />
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <MetricCard icon={<CheckCircleIcon />} iconBg="#10b981" value={data.closed} label={i18n.t("reports.cerrados")} />
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <MetricCard icon={<AssessmentIcon />} iconBg="#3b82f6" value={data.open} label={i18n.t("reports.enAtencion")} />
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <MetricCard icon={<HourglassEmptyIcon />} iconBg="#f59e0b" value={data.pending} label={i18n.t("reports.esperando")} />
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <MetricCard icon={<BlockIcon />} iconBg="#ef4444" value={data.noResponse ?? "—"} label={i18n.t("reports.sinRespuesta")} />
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <MetricCard icon={<TimerIcon />} iconBg="#8b5cf6"
                  value={data.avgResponseMinutes != null ? `${data.avgResponseMinutes} min` : "—"}
                  label={i18n.t("reports.respPromedio")}
                />
              </Grid>
            </Grid>

            {/* Fila 1: chats por día + estado */}
            <Grid container spacing={2} className={classes.panelRow}>
              <Grid item xs={12} md={8} className={classes.panelItem}>
                <Paper className={classes.chartPaper} elevation={0}>
                  <Typography variant="subtitle2" className={classes.chartTitle}>
                    {i18n.t("reports.chatsPorDia")}
                  </Typography>
                  <div className={classes.chartBox}>
                    <div className={classes.chartFill}>
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={data.daily} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="label" tick={{ fontSize: 11 }} interval={Math.max(0, Math.floor(data.daily.length / 10) - 1)} />
                          <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                          <ChartTooltip formatter={v => [v, i18n.t("reports.chats")]} labelFormatter={l => `${i18n.t("reports.fecha")}: ${l}`} />
                          <Line type="monotone" dataKey="count" stroke="#667eea" dot={false} strokeWidth={2} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </Paper>
              </Grid>

              <Grid item xs={12} md={4} className={classes.panelItem}>
                <Paper className={classes.chartPaper} elevation={0}>
                  <Typography variant="subtitle2" className={classes.chartTitle}>
                    {i18n.t("reports.estadoChats")}
                  </Typography>
                  <div className={classes.chartBox}>
                    <div className={classes.chartFill}>
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie data={statusPie} cx="50%" cy="45%" outerRadius="75%" dataKey="value">
                            {statusPie.map((_, i) => <Cell key={i} fill={PIE_COLORS[i]} />)}
                          </Pie>
                          <Legend formatter={pctLegend(statusPie)} />
                          <ChartTooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </Paper>
              </Grid>
            </Grid>

            {/* Fila 2: IA vs humano + por agente + por cola + por motivo */}
            <Grid container spacing={2} className={classes.panelRow}>
              {bottomPanels.map(key => (
                <Grid item key={key} xs={12} sm={bottomSm} md={bottomMd} className={classes.panelItem}>
                  {renderBottomPanel(key)}
                </Grid>
              ))}
            </Grid>
          </>
        )}
      </div>
    </MainContainer>
  );
};

export default Reports;
